import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  IDropshippingSettings,
  IMPORT_LIST,
  IOrdersList,
  IOrdersParams,
  ImportListParams,
  ORDERS,
  PRODUCTS,
  DROPSHIPPING_SETTINGS,
  BULK_CHECKOUT_SUMMARY,
  FETCH_PRODUCT_DATABASE,
} from "./types";
import {
  createSampleOrder,
  getCategories,
  getImportList,
  getOrders,
  getProducts,
  getDropshippingSettings,
  updateDropshippingSettings,
  cancelSubOrder,
  getBulkCheckoutSummary,
  updateImportListBulk,
  getProductsDatabase,
  getDashboard,
} from "./requests";
import {
  ICategories,
  IProductList,
  IProductParams,
} from "~/types/dropshipping";
import fromToastMessage from "~/helpers/handleToast";
import { getPresets } from "~/api/presets/requests";
import handleErrors from "~/helpers/handleErrors";
import { TFunction } from "i18next";

export const useGetProducts = (params?: IProductParams) =>
  useInfiniteQuery<IProductList>(
    [...PRODUCTS, params],
    ({ pageParam = 1 }) => getProducts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetProductDatabase = (params?: {
  page: number;
  items_per_page: number;
  filters?: Record<PropertyKey, any>;
}) => {
  return useQuery<IProductList>(
    [FETCH_PRODUCT_DATABASE, params],
    () => getProductsDatabase(params),
    {
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: () => {},
      onError: (error: any) => {
        let errorMessage = "Error loading data";
        if (error.response?.data?.error) {
          errorMessage = error.response?.data?.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        fromToastMessage("error", errorMessage);
      },
    }
  );
};

export const useGetCategories = () =>
  useQuery<Array<ICategories>>([], () => getCategories());

export const useGetOrders = (params?: IOrdersParams) =>
  useQuery<IOrdersList>([...ORDERS, params], () => getOrders(params));

export const useGetImportList = (params?: ImportListParams) =>
  useQuery<IProductList>([...IMPORT_LIST, params], () => getImportList(params));

export const useCreateSampleOrder = () => useMutation(createSampleOrder);

export const useDropshippingSettings = () =>
  useQuery<IDropshippingSettings>(DROPSHIPPING_SETTINGS, () =>
    getDropshippingSettings()
  );

export const useUpdateDropshippingSettings = () =>
  useMutation(updateDropshippingSettings);

export const useCancelSubOrder = () => useMutation(cancelSubOrder);

export const useGetBulkCheckoutSummary = (
  subOrderIds: number[] = [],
  orderType: string
) =>
  useQuery([...BULK_CHECKOUT_SUMMARY, orderType, ...subOrderIds], () =>
    getBulkCheckoutSummary(subOrderIds, orderType)
  );

export const useUpdateImportListBulk = () => useMutation(updateImportListBulk);

export const useGetDashboard = () => useQuery([], () => getDashboard());

export const prefetchPresets = async (t: TFunction, featureType: string) => {
  const cleanedType = featureType
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const toastMessages = {
    loading: t("dropshipping.loading_present_filter", {
      featureType: cleanedType,
    }),
    success: t("dropshipping.success_present_filter", {
      featureType: cleanedType,
    }),
    error: t("dropshipping.error_present_filter", { featureType: cleanedType }),
  };

  const { response } = await handleErrors(
    () => getPresets({ featureType }),
    toastMessages
  );

  return response ?? [];
};
